/**
 * Configuração centralizada de status para toda a aplicação
 * Define cores, labels, ícones e estilos para diferentes tipos de status
 */
import {
  CheckCircle,
  Pending,
  Cancel,
  Help,
  EventAvailable,
} from '@mui/icons-material';

/**
 * Status de Agendamento (Appointment)
 * Usado em: DashboardPage, ReportsPage, AppointmentCard
 */
export const APPOINTMENT_STATUS = {
  Pending: {
    label: 'Pendente',
    chipColor: 'warning',
    icon: Pending,
    bgTint: 'rgba(255, 152, 0, 0.08)',
    borderColor: 'warning.main',
  },
  Confirmed: {
    label: 'Confirmado',
    chipColor: 'info',
    icon: CheckCircle,
    bgTint: 'rgba(33, 150, 243, 0.08)',
    borderColor: 'info.main',
  },
  Attended: {
    label: 'Realizado',
    chipColor: 'success',
    icon: EventAvailable,
    bgTint: 'rgba(76, 175, 80, 0.08)',
    borderColor: 'success.main',
  },
  Cancelled: {
    label: 'Cancelado',
    chipColor: 'error',
    icon: Cancel,
    bgTint: 'rgba(244, 67, 54, 0.08)',
    borderColor: 'error.main',
  },
  NoShow: {
    label: 'Não Compareceu',
    chipColor: 'default',
    icon: Help,
    bgTint: 'rgba(158, 158, 158, 0.08)',
    borderColor: 'grey.400',
  },
};

/**
 * Status de Agendamento (Schedule)
 * Cores fixas do sistema, independentes de tema
 */
export const SCHEDULE_STATUS = {
  Active: {
    label: 'Ativa',
    borderColor: '#1b8a4c',
    opacity: 1,
    chipSx: {
      bgcolor: '#1b8a4c',
      color: '#fff',
      '& .MuiChip-label': { fontWeight: 600 },
    },
  },
  Inactive: {
    label: 'Inativa',
    borderColor: '#757575',
    opacity: 0.85,
    chipSx: {
      bgcolor: '#757575',
      color: '#fff',
      '& .MuiChip-label': { fontWeight: 600 },
    },
  },
  Scheduled: {
    label: 'Agendada',
    borderColor: '#3949ab',
    opacity: 1,
    chipSx: {
      bgcolor: '#3949ab',
      color: '#fff',
      '& .MuiChip-label': { fontWeight: 600 },
    },
  },
  Expired: {
    label: 'Expirada',
    borderColor: '#e65100',
    opacity: 0.9,
    chipSx: {
      bgcolor: '#e65100',
      color: '#fff',
      '& .MuiChip-label': { fontWeight: 600 },
    },
  },
  Deleted: {
    label: 'Excluída',
    borderColor: '#c62828',
    opacity: 0.65,
    chipSx: {
      bgcolor: '#c62828',
      color: '#fff',
      '& .MuiChip-label': { fontWeight: 600 },
    },
  },
};

/**
 * Status de Ticket (SupportTicket) — v2, máquina de estados "de quem é a bola"
 * (docs/PLANO-SUPPORT-TICKETS-V2.md §1.1).
 *
 * Chaves em camelCase string: a Api serializa enums como string via
 * JsonStringEnumConverter global (Api Program.cs) — NÃO como int, apesar do que os
 * comentários de DTO sugerem. Números mantidos só de defesa/histórico (nenhum caller
 * real depende deles hoje). O único componente que hoje exibe status de ticket ao vivo
 * é TicketList.jsx, que tem seu PRÓPRIO STATUS_MAP local (mesma correção aplicada lá).
 */
export const TICKET_STATUS = {
  open: { label: 'Aberto', chipColor: 'info' },
  inProgress: { label: 'Em Análise', chipColor: 'warning' },
  resolved: { label: 'Resolvido', chipColor: 'success' },
  closed: { label: 'Fechado', chipColor: 'default' },
  waitingCustomer: { label: 'Aguardando Cliente', chipColor: 'warning' },
  waitingSupport: { label: 'Aguardando Suporte', chipColor: 'error' },
  0: { label: 'Aberto', chipColor: 'info' },
  1: { label: 'Em Análise', chipColor: 'warning' },
  2: { label: 'Resolvido', chipColor: 'success' },
  3: { label: 'Fechado', chipColor: 'default' },
  4: { label: 'Aguardando Cliente', chipColor: 'warning' },
  5: { label: 'Aguardando Suporte', chipColor: 'error' },
};

/**
 * Acesso defensivo a TICKET_STATUS: protege a janela de deploy em que a Api já serve
 * status novo mas o Admin ainda não foi redeployado (ou vice-versa) — nunca deixa a UI
 * quebrar por um valor não mapeado.
 */
export const getTicketStatus = (status) =>
  TICKET_STATUS[status] ?? { label: 'Desconhecido', chipColor: 'default' };

/**
 * Prioridade de Ticket. Mesma ressalva de serialização do TICKET_STATUS acima
 * (Api manda string camelCase; números ficam de defesa/histórico).
 */
export const TICKET_PRIORITY = {
  low: { label: 'Baixa', chipColor: 'info', icon: '↓' },
  normal: { label: 'Normal', chipColor: 'default', icon: null },
  high: { label: 'Alta', chipColor: 'error', icon: '↑' },
  urgent: { label: 'Urgente', chipColor: 'error', icon: '⚡' },
  0: { label: 'Baixa', chipColor: 'info', icon: '↓' },
  1: { label: 'Normal', chipColor: 'default', icon: null },
  2: { label: 'Alta', chipColor: 'error', icon: '↑' },
  3: { label: 'Urgente', chipColor: 'error', icon: '⚡' },
};

/**
 * Mapa de cores para ReportsPage (gráficos)
 * Mapeia rótulos de status para códigos de cor hex
 */
export const STATUS_COLOR_MAP = {
  Confirmado: '#1976d2', // info.main
  Realizado: '#388e3c', // success.main
  Cancelado: '#d32f2f', // error.main
  Pendente: '#f57c00', // warning.main
};

/**
 * Helper para obter a cor de um status em AppointmentCard
 * Utiliza propriedades de APPOINTMENT_STATUS para render dinâmico
 */
export const getAppointmentStatusStyles = (status) => {
  const statusMap = {
    pending: APPOINTMENT_STATUS.Pending,
    confirmed: APPOINTMENT_STATUS.Confirmed,
    completed: APPOINTMENT_STATUS.Attended,
    no_show: APPOINTMENT_STATUS.NoShow,
    cancelled: APPOINTMENT_STATUS.Cancelled,
  };

  const config = statusMap[status];
  return {
    borderColor: config?.borderColor || 'divider',
    bgcolor: config?.bgTint || 'background.default',
    chipColor: config?.chipColor || 'default',
    opacity: status === 'cancelled' ? 0.6 : 1,
  };
};

/**
 * Helper para obter o label de um status em AppointmentCard
 */
export const getAppointmentStatusLabel = (status) => {
  const labelMap = {
    pending: 'Não Confirmado',
    confirmed: 'Confirmado',
    completed: 'Concluído',
    no_show: 'Não Compareceu',
    cancelled: 'Cancelado',
  };
  return labelMap[status] || 'Desconhecido';
};
